import React, { useRef } from 'react';
import { SelectOption } from 'global';
import { Control, FieldValues, Path } from 'react-hook-form';
import { useLazyQuery } from '@apollo/client';
import { AsyncAutocompleteField } from 'components/form';

import { internalInventoryApi } from '../api';

export interface InventoryEquipment {
  id: string;
  equipment: {
    id: string;
    assets: { id: string; source: string }[];
    price: {
      retailPrice: number | null;
      wholesaleCost: number | null;
      totalWholesaleCost: number | null;
    };
    details: {
      make: string;
      vin: string;
      year: string;
      model: string;
      name: string;
      class: string;
      mileage: string;
      stockId: string;
      condition: string;
      description: string;
    };

    attachments: any[];
  };
}

interface InventorySearchResponse {
  result: {
    success: boolean;
    error: string | null;
    list: InventoryEquipment[];
  };
}

interface Props<TFormValues extends FieldValues> {
  name: Path<TFormValues>;
  label?: string;
  control: Control<TFormValues>;
  extraAction?: (item: InventoryEquipment) => void;
}

export const InventorySearchField = <TFormValues extends FieldValues>({
  control,
  extraAction,
  name,
  label = 'Search by Stock#, VIN, make, model, or name',
}: Props<TFormValues>) => {
  const [searchInventory] = useLazyQuery<InventorySearchResponse>(internalInventoryApi.getFullList);
  const inventoryListRef = useRef<InventoryEquipment[]>([]);

  const handleFetchOptions = async (search: string): Promise<SelectOption[]> => {
    const { data } = await searchInventory({
      variables: {
        input: {
          search: search.trim().toLowerCase(),
        },
      },
    });

    const list = data?.result?.list ?? [];

    inventoryListRef.current = list;

    return list.map(item => ({ value: item.id, label: item.equipment.details.name }));
  };

  const handleExtraAction = (selected: SelectOption[]) => {
    const selectedOption = Array.isArray(selected) ? selected[0] : selected;
    if (!selectedOption) return;

    const item = inventoryListRef.current.find(i => i.id === selectedOption.value);

    if (!item) return;

    extraAction?.(item);
  };

  return (
    <AsyncAutocompleteField
      name={name}
      label={label}
      control={control}
      extraAction={handleExtraAction}
      fetchOptions={handleFetchOptions}
    />
  );
};
